/**
 * useBugReport hook
 * Handles bug report submission with game context and device info
 */

import { useState, useCallback } from 'react';
import { getGameContext, collectReportData, resizeImageToFit } from '../utils/bugReportContext';
import logger from '../utils/logger';

interface UseBugReportReturn {
  submitBugReport: (description: string, screenshot?: File) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  success: boolean;
  reset: () => void;
}

export const useBugReport = (): UseBugReportReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const submitBugReport = useCallback(async (description: string, screenshot?: File) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      // Collect game context
      const gameContext = getGameContext();

      // Collect device and build info
      const reportData = await collectReportData();

      // Prepare form data
      const formData = new FormData();

      // Add data as JSON string
      formData.append('data', JSON.stringify({
        description,
        gameContext,
        reportData
      }));

      // Add screenshot if provided
      if (screenshot) {
        // Resize image to fit within 1MB
        const resizedScreenshot = await resizeImageToFit(screenshot);
        formData.append('screenshot', resizedScreenshot);
      }

      // Submit to API
      const response = await fetch('/api/bugs', {
        method: 'POST',
        body: formData,
        credentials: 'include' // Include session cookie
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit bug report');
      }

      const result = await response.json();

      logger.info('Bug report submitted successfully', {
        bugReportId: result.bugReport.id
      });

      setSuccess(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit bug report';
      setError(errorMessage);
      logger.error('Error submitting bug report', { error: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return {
    submitBugReport,
    isSubmitting,
    error,
    success,
    reset
  };
};
